import InputField from '../InputField'
import CheckboxField from '../CheckboxField'
import SelectField from '../SelectField'
import { checkStoreIs } from '@helpers/env'
import { SetSelectionArgs } from '@components/ui/formElements/Dropdown/List'
import styles from './styles.module.scss'
import { ISO_3166_COUNTRIES } from '@consts/index'
import { AddressSchema } from '../../../config'
import { z } from 'zod'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { getProvinces, ProvinceData } from '@api/apiCalls'
import trackError from '@helpers/trackError'
import { FieldValues, UseFormSetValue } from 'react-hook-form'

type SetValueFunction<T extends FieldValues> = UseFormSetValue<T>

type AddressFormFieldsProps = {
  formValues: AddAddressData | UpdateAddressData
  formSetFieldValue: SetValueFunction<AddAddressData | UpdateAddressData>
  setIsProvincesLoading: Dispatch<SetStateAction<boolean>>
}

export interface FieldStateMethods {
  getIsProvincesLoading: () => boolean
}

export function getProvinceList(provinceData: ProvinceData | null) {
  if (!provinceData || !provinceData.provinces) return

  return provinceData.provinces.map(({ name, code }) => {
    return {
      value: code,
      label: name,
      availability: true
    }
  })
}

export const AddAddressSchema = AddressSchema.omit({ id: true }).extend({
  territoryCode: z.string(),
  defaultAddress: z.boolean()
})

export type AddAddressData = z.infer<typeof AddAddressSchema>

export const UpdateAddressSchema = AddressSchema.extend({
  defaultAddress: z.boolean(),
  territoryCode: z.string()
})

export type UpdateAddressData = z.infer<typeof UpdateAddressSchema>

function getCountryList() {
  return Object.entries(ISO_3166_COUNTRIES).map(
    ([_, { alpha2, countryName }]) => {
      return {
        value: alpha2,
        label: countryName,
        availability: true
      }
    }
  )
}

export const AddressFormFields = ({
  formValues,
  formSetFieldValue,
  setIsProvincesLoading
}: AddressFormFieldsProps) => {
  const [provinceData, setProvinceData] = useState<ProvinceData | null>(null)

  useEffect(() => {
    const territoryCode = formValues.territoryCode
    if (territoryCode) {
      setIsProvincesLoading(true)

      getProvinces(territoryCode as string)
        .then(data => {
          setProvinceData(data)
        })
        .catch(err => {
          const error = err as Error
          trackError(
            new Error(
              `Failed to get province data on edit address - ${error.message}`
            ),
            {
              error,
              section: 'Account Page'
            }
          )
        })
        .finally(() => {
          setIsProvincesLoading(false)
        })
    }
  }, [formValues.territoryCode, setIsProvincesLoading])

  const handleSelectProvince = async (args: SetSelectionArgs) => {
    formSetFieldValue('zoneCode', args.zoneCode as string, {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true
    })
  }

  const handleSelectCountry = async (args: SetSelectionArgs) => {
    formSetFieldValue('territoryCode', args.territoryCode as string, {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true
    })
  }

  const countries = getCountryList()
  const provinces = getProvinceList(provinceData)

  const defaultCountry =
    countries.find(country => country.value === formValues?.territoryCode)
      ?.label || 'Select Country'

  return (
    <div className={styles.AccountForm__content}>
      <InputField name="First Name" id="firstName" type="text" required />
      <InputField name="Last Name" id="lastName" type="text" required />
      <InputField name="Address Line 1" id="address1" type="text" required />
      <InputField name="Address Line 2" id="address2" type="text" />
      <InputField name="City" id="city" type="text" required />
      <SelectField
        variant="reverse"
        id="territoryCode"
        defaultValue={defaultCountry}
        list={countries}
        selectedValue={formValues?.territoryCode}
        label="Country"
        required={true}
        handleSelectOption={handleSelectCountry}
      />
      {provinceData && provinces && (
        <SelectField
          variant="reverse"
          id="zoneCode"
          list={provinces}
          selectedValue={formValues?.zoneCode || ''}
          defaultValue={`Select ${provinceData.label}`}
          label={provinceData.label || 'Province'}
          required={true}
          handleSelectOption={handleSelectProvince}
        />
      )}
      <InputField
        name={checkStoreIs('us') ? 'Zip Code' : 'Postal Code'}
        id="zip"
        type="text"
        required
      />
      <CheckboxField name="Primary Address?" id="defaultAddress" />
    </div>
  )
}
